/**
 * Word-level diff for the QG fix-batch review panes (operator direction
 * 2026-08-31: highlight the changes on BOTH sides so the edit is legible,
 * not a spot-the-difference exercise).
 *
 * Tokenises on whitespace (keeping the whitespace tokens so reflow is
 * byte-faithful), trims the common prefix/suffix, and runs an LCS over the
 * changed middle. Articles are a few thousand tokens; the prefix/suffix trim
 * keeps the DP small for the typical few-word edit. If the middle is still
 * huge (pathological rewrite), falls back to one del/add block pair — still
 * correct, just coarser.
 */

export type DiffOp = { type: 'eq' | 'del' | 'add'; text: string };

const MAX_DP_CELLS = 4_000_000;

function tokenize(s: string): string[] {
  return s.split(/(\s+)/).filter((t) => t.length > 0);
}

export function diffWords(before: string, after: string): DiffOp[] {
  if (before === after) return before ? [{ type: 'eq', text: before }] : [];
  const a = tokenize(before);
  const b = tokenize(after);

  let start = 0;
  while (start < a.length && start < b.length && a[start] === b[start]) start++;
  let endA = a.length;
  let endB = b.length;
  while (endA > start && endB > start && a[endA - 1] === b[endB - 1]) {
    endA--;
    endB--;
  }

  const ops: DiffOp[] = [];
  if (start > 0) ops.push({ type: 'eq', text: a.slice(0, start).join('') });

  const midA = a.slice(start, endA);
  const midB = b.slice(start, endB);

  if (midA.length * midB.length > MAX_DP_CELLS) {
    if (midA.length) ops.push({ type: 'del', text: midA.join('') });
    if (midB.length) ops.push({ type: 'add', text: midB.join('') });
  } else if (midA.length || midB.length) {
    // LCS table over the middle tokens.
    const rows = midA.length + 1;
    const cols = midB.length + 1;
    const lcs = new Uint32Array(rows * cols);
    for (let i = midA.length - 1; i >= 0; i--) {
      for (let j = midB.length - 1; j >= 0; j--) {
        lcs[i * cols + j] =
          midA[i] === midB[j]
            ? lcs[(i + 1) * cols + j + 1] + 1
            : Math.max(lcs[(i + 1) * cols + j], lcs[i * cols + j + 1]);
      }
    }
    let i = 0;
    let j = 0;
    let run: DiffOp | null = null;
    const push = (type: DiffOp['type'], text: string) => {
      if (run && run.type === type) run.text += text;
      else {
        if (run) ops.push(run);
        run = { type, text };
      }
    };
    while (i < midA.length && j < midB.length) {
      if (midA[i] === midB[j]) {
        push('eq', midA[i]);
        i++;
        j++;
      } else if (lcs[(i + 1) * cols + j] >= lcs[i * cols + j + 1]) {
        push('del', midA[i]);
        i++;
      } else {
        push('add', midB[j]);
        j++;
      }
    }
    while (i < midA.length) push('del', midA[i++]);
    while (j < midB.length) push('add', midB[j++]);
    if (run) ops.push(run);
  }

  if (endA < a.length) ops.push({ type: 'eq', text: a.slice(endA).join('') });
  return ops;
}

/** True when the diff contains any change (guards rendering empty marks). */
export function hasChanges(ops: DiffOp[]): boolean {
  return ops.some((o) => o.type !== 'eq');
}
